import React from "react";
import singpassLogo from "../../assets/singpass/singpass-full-colour.png";
import {
  PROPERTY_CO_OWNER_RELATIONSHIPS,
  createEmptyPropertyCoOwner,
} from "../../utils/propertyLoanOwnership";

export default function PropertyLoanCoOwners({
  owners,
  applicantCpf,
  totalCpf,
  mainCpfChanged,
  editCpf,
  onApplicantCpfChange,
  onChange,
  onSingleOwner,
  MoneyInput,
  formatMoney,
  validationAttempt = 0,
}) {
  const updateOwner = (index, changes) =>
    onChange(
      owners.map((owner, i) =>
        i === index ? { ...owner, ...changes } : owner,
      ),
    );
  const invalid = (owner, field) =>
    validationAttempt > 0 &&
    (field === "cpfUsage"
      ? owner.cpfUsage == null ||
        owner.cpfUsage === "" ||
        !Number.isFinite(Number(owner.cpfUsage)) ||
        Number(owner.cpfUsage) < 0
      : field === "relationship"
        ? !PROPERTY_CO_OWNER_RELATIONSHIPS.includes(owner.relationship)
        : !owner[field]?.trim());
  const fieldProps = (owner, field) => ({
    "aria-required": true,
    "aria-invalid": invalid(owner, field) || undefined,
    "aria-describedby": invalid(owner, field)
      ? "pl-ownership-error"
      : undefined,
    className: invalid(owner, field) ? "pl-field-invalid" : "",
  });
  return (
    <div className="pl-coowners">
      <div className="pl-coowner-callout">
        <b>👥 Jointly owned property: add your co-owner's CPF usage</b>
        <span>
          Each owner's CPF has to be refunded when the property is sold. Key in
          your spouse's or co-owner's figure so your estimate is accurate.
        </span>
      </div>
      <div className="pl-coowner-main">
        <span className="pl-avatar">You</span>
        <div>
          <b>You (main applicant)</b>
          <small>CPF housing usage</small>
        </div>
        <b>{formatMoney(applicantCpf)}</b>
        <span
          className={
            mainCpfChanged ? "pl-you" : "pl-source pl-source-with-logo"
          }
        >
          {mainCpfChanged ? (
            "You updated"
          ) : (
            <img
              className="pl-singpass-logo"
              src={singpassLogo}
              alt="Singpass"
              width="80"
              height="24"
            />
          )}
        </span>
      </div>
      {editCpf && (
        <div className="pl-coowner-applicant-edit">
          <label htmlFor="pl-applicant-cpf">Your CPF housing usage</label>
          <MoneyInput
            id="pl-applicant-cpf"
            value={applicantCpf}
            onChange={onApplicantCpfChange}
          />
        </div>
      )}
      <div className="pl-coowner-tools">
        <button
          type="button"
          className="pl-text-button"
          onClick={onSingleOwner}
        >
          ← Not jointly owned? Switch back to single owner
        </button>
        <button
          type="button"
          className="pl-text-button"
          onClick={() => onChange([createEmptyPropertyCoOwner()])}
        >
          ↺ Reset co-owners
        </button>
      </div>
      {owners.map((owner, index) => (
        <section
          className="pl-coowner"
          key={index}
          aria-labelledby={`pl-coowner-title-${index}`}
        >
          <div className="pl-coowner-heading">
            <span className="pl-avatar">CO</span>
            <div>
              <b id={`pl-coowner-title-${index}`}>Co-owner {index + 1}</b>
              <small>Key in their CPF housing usage for this property</small>
            </div>
            <div className="pl-coowner-actions">
              <button
                type="button"
                className="pl-text-button"
                aria-label={`Clear co-owner ${index + 1}`}
                onClick={() => updateOwner(index, createEmptyPropertyCoOwner())}
              >
                ↺ Clear
              </button>
              <button
                type="button"
                className="pl-text-button pl-danger"
                aria-label={`Remove co-owner ${index + 1}`}
                onClick={() => onChange(owners.filter((_, i) => i !== index))}
              >
                ✕ Remove
              </button>
            </div>
          </div>
          <div className="pl-coowner-grid" key={validationAttempt}>
            <label htmlFor={`pl-coowner-name-${index}`}>
              <span className="pl-required-label">Full name (as per NRIC)</span>
              <input
                {...fieldProps(owner, "name")}
                id={`pl-coowner-name-${index}`}
                placeholder="e.g. Tan Mei Ling"
                maxLength={255}
                value={owner.name}
                onChange={(event) =>
                  updateOwner(index, { name: event.target.value })
                }
              />
            </label>
            <label htmlFor={`pl-coowner-relationship-${index}`}>
              <span className="pl-required-label">Relationship</span>
              <select
                {...fieldProps(owner, "relationship")}
                id={`pl-coowner-relationship-${index}`}
                value={owner.relationship}
                onChange={(event) =>
                  updateOwner(index, { relationship: event.target.value })
                }
              >
                {PROPERTY_CO_OWNER_RELATIONSHIPS.map((relationship) => (
                  <option key={relationship} value={relationship}>
                    {relationship}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor={`pl-coowner-phone-${index}`}>
              <span className="pl-required-label">Mobile number</span>
              <input
                {...fieldProps(owner, "phone")}
                id={`pl-coowner-phone-${index}`}
                type="tel"
                placeholder="+65 9123 4567"
                maxLength={32}
                value={owner.phone}
                onChange={(event) =>
                  updateOwner(index, { phone: event.target.value })
                }
              />
            </label>
            <div className="pl-coowner-cpf">
              <label htmlFor={`pl-coowner-cpf-${index}`}>
                <span className="pl-required-label">
                  Their CPF housing usage
                </span>
              </label>
              <MoneyInput
                id={`pl-coowner-cpf-${index}`}
                required
                invalid={invalid(owner, "cpfUsage")}
                className={invalid(owner, "cpfUsage") ? "pl-field-invalid" : ""}
                value={owner.cpfUsage}
                onChange={(cpfUsage) => updateOwner(index, { cpfUsage })}
              />
            </div>
          </div>
        </section>
      ))}
      <button
        type="button"
        className="pl-add-coowner"
        onClick={() => onChange([...owners, createEmptyPropertyCoOwner()])}
      >
        ＋ Add co-owner
      </button>
      <div className="pl-coowner-total">
        <span>Total CPF housing usage for this property</span>
        <b>{formatMoney(totalCpf)}</b>
      </div>
    </div>
  );
}
